import type { Tutorial } from "@/lib/tutorials";

import { useState } from "react";
import { Button, Dropdown, Separator, Typography } from "@heroui/react";
import { Icon } from "@iconify/react";
import { useNavigate } from "react-router-dom";

import { TutorialPlayer } from "./tutorial-player";

import {
  findTutorialById,
  tutorialAreaPath,
  tutorialMeta,
} from "@/lib/tutorials";
import { isTutorialsEnabled } from "@/config/constants";

interface HowItWorksButtonProps {
  // Ids from tutorials.generated.ts, one per recording, in display order.
  tutorialIds: readonly number[];
  isIconOnly?: boolean;
}

export const HowItWorksButton = ({
  tutorialIds,
  isIconOnly = false,
}: HowItWorksButtonProps) => {
  const [playing, setPlaying] = useState<Tutorial | null>(null);
  const navigate = useNavigate();
  const tutorials = tutorialIds.flatMap((id) => findTutorialById(id) ?? []);
  const group = tutorials[0]?.group;
  const allLabel = `All ${group} tutorials`;

  if (!tutorials.length || !isTutorialsEnabled()) return null;

  const button = (
    <Button
      aria-label="How it works"
      isIconOnly={isIconOnly}
      size="sm"
      variant="ghost"
      onPress={
        tutorials.length === 1 ? () => setPlaying(tutorials[0]) : undefined
      }
    >
      <Icon aria-hidden="true" icon="solar:play-circle-linear" width={18} />
      {!isIconOnly && "How it works"}
    </Button>
  );

  return (
    <>
      {playing && (
        <TutorialPlayer
          key={playing.id}
          tutorial={playing}
          onClose={() => setPlaying(null)}
        />
      )}
      {tutorials.length === 1 ? (
        button
      ) : (
        <Dropdown>
          {button}
          <Dropdown.Popover
            className="w-96 max-w-[calc(100vw-2rem)]"
            placement="bottom end"
          >
            <Dropdown.Menu aria-label="How it works">
              <Dropdown.Section>
                {tutorials.map((tutorial) => (
                  <Dropdown.Item
                    key={tutorial.id}
                    id={tutorial.id}
                    textValue={tutorial.title}
                    onAction={() => setPlaying(tutorial)}
                  >
                    <div className="min-w-0 py-1">
                      <Typography className="whitespace-normal" type="body-sm">
                        {tutorial.title}
                      </Typography>
                      <Typography
                        className="whitespace-normal"
                        color="muted"
                        type="body-xs"
                      >
                        {tutorialMeta(tutorial)}
                      </Typography>
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Section>
              <Separator />
              <Dropdown.Item
                id="all-tutorials"
                textValue={allLabel}
                onAction={() => navigate(tutorialAreaPath(group))}
              >
                <Icon
                  aria-hidden="true"
                  icon="solar:book-2-linear"
                  width={18}
                />
                {allLabel}
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown.Popover>
        </Dropdown>
      )}
    </>
  );
};
